import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { validateFormAction } from '../actions'
import { initFormData, updateFormDataField } from '../formSlice'
import { createFormDataSelector, getInitFormState } from '../helpers'
import { THandleChangeParams, TUseGetFormDataResult } from '../models'

/*
 * This hook return form data by key and handler to change data by field name
 * */
export const useGetFormData = <T>(formKey: string, initData?: T): TUseGetFormDataResult<T> => {
  const dispatch = useAppDispatch()
  const selectModifiedData = useMemo(() => createFormDataSelector<T>(formKey), [formKey])
  const formData = useSelector(selectModifiedData)

  const handleChange = useCallback(
    <K extends keyof T>({
      field,
      value,
      doRevalidate,
      ignoreModified,
    }: THandleChangeParams<T, K>) => {
      dispatch(updateFormDataField({ formKey, field, value, ignoreModified } as any))
      if (doRevalidate) {
        dispatch(validateFormAction(formKey))
      }
    },
    [dispatch, formKey]
  )

  useEffect(() => {
    if (!formData && initData !== undefined) {
      dispatch(initFormData({ formKey, formData: initData }))
    }
  }, [dispatch, formData, formKey, initData])

  return [formData || getInitFormState<T>(initData), handleChange]
}
